import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Target,
  DollarSign,
  TrendingUp,
  CheckCircle,
  Users,
  Briefcase,
  Clock,
  AlertCircle
} from 'lucide-react';
import { format } from 'date-fns';

interface DashboardProps {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  token: string;
}

interface DashboardStats {
  totalOpportunities: number;
  totalPipelineValue: number;
  activeProjects: number;
  activeClients: number;
  openTickets: number;
  recentActivity: Array<{
    type: string;
    title: string;
    description: string;
    timestamp: string;
  }>;
}

export default function Dashboard({ user, token }: DashboardProps) {
  const { data: stats, isLoading } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      // Fetch real stats from API
      const [oppsRes, projectsRes, clientsRes, ticketsRes] = await Promise.all([
        fetch(`${import.meta.env.VITE_API_URL || 'https://api.cyvine.com'}/api/opportunities`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${import.meta.env.VITE_API_URL || 'https://api.cyvine.com'}/api/projects`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${import.meta.env.VITE_API_URL || 'https://api.cyvine.com'}/api/clients`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${import.meta.env.VITE_API_URL || 'https://api.cyvine.com'}/api/support`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
      ]);

      const opportunities = oppsRes.ok ? await oppsRes.json() : [];
      const projects = projectsRes.ok ? await projectsRes.json() : [];
      const clients = clientsRes.ok ? await clientsRes.json() : [];
      const tickets = ticketsRes.ok ? await ticketsRes.json() : [];

      // Calculate pipeline value from opportunities
      const pipelineValue = Array.isArray(opportunities) 
        ? opportunities.reduce((sum: number, opp: any) => sum + (opp.estimatedValue || 0), 0)
        : 0;

      // Count active items
      const activeProjects = Array.isArray(projects) 
        ? projects.filter((p: any) => p.status === 'active' || p.status === 'in_progress').length
        : 0;
      
      const activeClients = Array.isArray(clients)
        ? clients.filter((c: any) => c.status === 'active').length
        : 0;

      const openTickets = Array.isArray(tickets)
        ? tickets.filter((t: any) => t.status !== 'resolved' && t.status !== 'closed').length
        : 0;

      // Build recent activity from real data
      const recentActivity: DashboardStats['recentActivity'] = [];
      
      if (Array.isArray(opportunities)) {
        opportunities.slice(0, 2).forEach((opp: any) => {
          recentActivity.push({
            type: 'opportunity',
            title: 'Opportunity',
            description: `${opp.company} - ${opp.title}`,
            timestamp: opp.createdAt
          });
        });
      }

      if (Array.isArray(projects)) {
        projects.slice(0, 2).forEach((proj: any) => {
          recentActivity.push({
            type: 'project',
            title: 'Project',
            description: proj.name || proj.title,
            timestamp: proj.createdAt
          });
        });
      }

      // Sort by timestamp and take top 5
      recentActivity.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      return {
        totalOpportunities: Array.isArray(opportunities) ? opportunities.length : 0,
        totalPipelineValue: pipelineValue,
        activeProjects,
        activeClients,
        openTickets,
        recentActivity: recentActivity.slice(0, 5)
      };
    }
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome banner */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold">Welcome back, {user.firstName}!</h1>
        <p className="mt-1 text-blue-100">
          Here's what's happening with your business today.
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Target className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Opportunities</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.totalOpportunities || 0}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Pipeline Value</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats?.totalPipelineValue || 0)}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Briefcase className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Active Projects</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.activeProjects || 0}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-orange-100 rounded-lg">
              <Users className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Active Clients</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.activeClients || 0}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-red-100 rounded-lg">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Open Tickets</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.openTickets || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2 card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
          {stats?.recentActivity && stats.recentActivity.length > 0 ? (
            <div className="space-y-4">
              {stats.recentActivity.map((activity, index) => (
                <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`p-2 rounded-lg ${
                    activity.type === 'opportunity' ? 'bg-blue-100' :
                    activity.type === 'project' ? 'bg-purple-100' : 'bg-green-100'
                  }`}>
                    {activity.type === 'opportunity' && <Target className="h-4 w-4 text-blue-600" />}
                    {activity.type === 'project' && <Briefcase className="h-4 w-4 text-purple-600" />}
                    {activity.type === 'ticket' && <CheckCircle className="h-4 w-4 text-green-600" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                    <p className="text-sm text-gray-500">{activity.description}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      <Clock className="h-3 w-3 inline mr-1" />
                      {format(new Date(activity.timestamp), 'MMM d, h:mm a')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Target className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="font-medium">No activity yet</p>
              <p className="text-sm mt-1">Create your first opportunity to get started!</p>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <button className="w-full btn btn-primary flex items-center justify-center gap-2">
              <Target className="h-4 w-4" />
              New Opportunity
            </button>
            <button className="w-full btn btn-secondary flex items-center justify-center gap-2">
              <Briefcase className="h-4 w-4" />
              Create Project
            </button>
            <button className="w-full btn btn-secondary flex items-center justify-center gap-2">
              <Users className="h-4 w-4" />
              Add Client
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-3">System Status</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">API Server</span>
                <span className="badge badge-green">Online</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Database</span>
                <span className="badge badge-green">Connected</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Sales Kernel</span>
                <span className="badge badge-green">Active</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}